import React from 'react';
import {
  Typography,
  Container,
  Box,
  Paper,
  Grid,
  useTheme,
  Chip,
  keyframes,
  styled
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  ArrowBack,
  Business,
  ContactMail,
  Phone,
  Public,
  Gavel,
  AccountCircle,
  Security
} from '@mui/icons-material';

// Gradient animation for title
const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const AnimatedTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
  backgroundSize: '200% 200%',
  animation: `${gradient} 4s ease infinite`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700,
}));

const ImpressumPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        py: 8
      }}
    >
      {/* Animated Background Layers */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 20%, ${theme.palette.primary.main}12 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${theme.palette.secondary.main}10 0%, transparent 50%),
            radial-gradient(circle at 60% 40%, ${theme.palette.info.main}08 0%, transparent 50%)
          `,
          animation: `${pulse} 8s ease-in-out infinite`,
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ mb: 4 }}>
          <Link
            to="/"
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              marginBottom: '1rem'
            }}
          >
            <ArrowBack sx={{ mr: 1 }} />
            Zurück zur Startseite
          </Link>

          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              label="⚖️ Impressum"
              color="primary"
              variant="outlined"
              sx={{
                mb: 3,
                fontSize: '1rem',
                py: 1,
                px: 3,
                fontWeight: 600,
                backdropFilter: 'blur(10px)',
                background: `${theme.palette.primary.main}15`,
                border: `2px solid ${theme.palette.primary.main}50`,
              }}
            />
            <AnimatedTitle variant="h2" gutterBottom>
              Impressum
            </AnimatedTitle>
            <Typography variant="h6" color="text.secondary" sx={{ opacity: 0.9 }}>
              Rechtliche Informationen nach § 5 TMG
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.primary.main}08 100%)`,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `1px solid ${theme.palette.primary.main}30`,
                boxShadow: `0 8px 32px ${theme.palette.primary.main}20`,
              }}
            >
              <Typography variant="h4" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                <Business sx={{ mr: 2 }} />
                Anbieter
              </Typography>

              <Box sx={{
                p: 3,
                border: `2px solid ${theme.palette.primary.main}30`,
                borderRadius: 2,
                background: `${theme.palette.primary.main}10`,
                mb: 3
              }}>
                <Typography variant="h6" gutterBottom color="primary">
                  <strong>Smart Storage Checker</strong>
                </Typography>
                <Typography paragraph>
                  Ein Service der InnCharge GmbH<br />
                  Innovative Hybrid-Energiespeicher-Lösungen
                </Typography>
                <Typography>
                  <strong>Anschrift:</strong><br />
                  InnCharge GmbH<br />
                  [Straße und Hausnummer]<br />
                  [PLZ] [Ort]<br />
                  [Land]
                </Typography>
              </Box>

              <Typography variant="h5" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
                Geschäftsführung
              </Typography>
              <Typography paragraph>
                [Name Geschäftsführer 1]<br />
                [Name Geschäftsführer 2]
              </Typography>

              <Typography variant="h5" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
                Firmenbuch
              </Typography>
              <Typography paragraph>
                Firmenbuchgericht: [zuständiges Gericht]<br />
                Firmenbuchnummer: [FN Nummer]<br />
                UID-Nummer: [UID Nummer]<br />
                Unternehmensgegenstand: Entwicklung und Vertrieb von Energiespeichersystemen
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.secondary.main}08 100%)`,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `1px solid ${theme.palette.secondary.main}30`,
                boxShadow: `0 8px 32px ${theme.palette.secondary.main}20`,
              }}
            >
              <Typography variant="h4" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                <ContactMail sx={{ mr: 2 }} />
                Kontakt
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                  <Phone sx={{ mr: 1, color: theme.palette.primary.main }} />
                  Telefon
                </Typography>
                <Typography paragraph>
                  [Telefonnummer]<br />
                  Montag - Freitag: [Geschäftszeiten]
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                  <ContactMail sx={{ mr: 1, color: theme.palette.primary.main }} />
                  E-Mail
                </Typography>
                <Typography paragraph>
                  <strong>Allgemeine Anfragen:</strong><br />
                  [E-Mail Adresse]<br /><br />
                  <strong>Technischer Support:</strong><br />
                  [Support E-Mail]<br /><br />
                  <strong>Datenschutz:</strong><br />
                  [Datenschutz E-Mail]
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                  <Public sx={{ mr: 1, color: theme.palette.primary.main }} />
                  Internet
                </Typography>
                <Typography>
                  [Website URL]<br />
                  www.smartstoragechecker.com (Analyse-Tool)
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.info.main}08 100%)`,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `1px solid ${theme.palette.info.main}30`,
                boxShadow: `0 8px 32px ${theme.palette.info.main}20`,
              }}
            >
              <Typography variant="h4" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                <AccountCircle sx={{ mr: 2 }} />
                Verantwortlich für den Inhalt
              </Typography>
              <Typography paragraph>
                Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:
              </Typography>
              <Box sx={{
                p: 3,
                border: `2px solid ${theme.palette.info.main}30`,
                borderRadius: 2,
                background: `${theme.palette.info.main}10`
              }}>
                <Typography>
                  <strong>[Name des Verantwortlichen]</strong><br />
                  InnCharge GmbH<br />
                  [Straße und Hausnummer]<br />
                  [PLZ] [Ort]<br />
                  [Land]
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.warning.main}08 100%)`,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `1px solid ${theme.palette.warning.main}30`,
                boxShadow: `0 8px 32px ${theme.palette.warning.main}20`,
              }}
            >
              <Typography variant="h4" gutterBottom color="primary" fontWeight={700}>
                Haftungsausschluss
              </Typography>

              <Typography variant="h5" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
                Haftung für Inhalte
              </Typography>
              <Typography paragraph>
                Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten
                nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als
                Diensteanbieter jedoch nicht unter der Verpflichtung, übermittelte oder gespeicherte
                fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine
                rechtswidrige Tätigkeit hinweisen.
              </Typography>

              <Typography variant="h5" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
                Haftung für Links
              </Typography>
              <Typography paragraph>
                Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen
                Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen.
                Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber
                der Seiten verantwortlich.
              </Typography>

              <Typography variant="h5" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
                Analyseergebnisse
              </Typography>
              <Typography paragraph>
                Die durch unseren Smart Storage Checker generierten Analyseergebnisse dienen ausschließlich
                der ersten Orientierung und stellen keine verbindliche Beratung dar. Für konkrete
                Investitionsentscheidungen empfehlen wir eine individuelle Fachberatung.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `2px solid ${theme.palette.primary.main}30`,
                boxShadow: `0 8px 32px ${theme.palette.primary.main}25`,
              }}
            >
              <Typography variant="h4" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                <Security sx={{ mr: 2 }} />
                Urheberrecht & Technologie
              </Typography>

              <Typography variant="h5" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
                Urheberrecht
              </Typography>
              <Typography paragraph>
                Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen
                dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art
                der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen
                Zustimmung des jeweiligen Autors bzw. Erstellers.
              </Typography>

              <Typography variant="h5" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
                Technische Hinweise
              </Typography>
              <Typography paragraph>
                Diese Website nutzt moderne Web-Technologien und ist optimiert für aktuelle Browser.
                Für die volle Funktionalität wird JavaScript benötigt. Die Analyse-Algorithmen basieren
                auf bewährten mathematischen Modellen und werden kontinuierlich weiterentwickelt.
              </Typography>

              <Typography variant="h5" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
                Open Source Komponenten
              </Typography>
              <Typography>
                Diese Anwendung nutzt verschiedene Open Source Bibliotheken. Eine vollständige Liste
                der verwendeten Komponenten und deren Lizenzen finden Sie in unserer Dokumentation
                oder auf Anfrage.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Stand: {new Date().toLocaleDateString('de-DE')} |
            Alle Angaben ohne Gewähr |
            <Link to="/datenschutz" style={{ color: theme.palette.primary.main, textDecoration: 'none', marginLeft: '0.5rem' }}>
              Datenschutzerklärung
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ImpressumPage;
